import { type Lang } from "./series";

// Google My Maps embed URL — festas-lisboa-2026 map.
const MAP_EMBED_URL =
  "https://www.google.com/maps/d/embed?mid=1SyIeIVFSRGc6rgV2Ai78dmcXayE-94Q";

type Props = {
  lang?: string;
};

const STR: Record<
  Lang,
  {
    heading: string;
    description: string;
    iframeTitle: string;
    openMap: string;
    missingEmbed: string;
    missingHint: string;
  }
> = {
  pt: {
    heading: "Mapa dos arraiais",
    description:
      "Vê num só mapa os principais pontos das Festas de Lisboa para planeares o teu roteiro por bairro.",
    iframeTitle: "Mapa dos arraiais das Festas de Lisboa",
    openMap: "Abrir no Google Maps",
    missingEmbed: "Mapa ainda não configurado",
    missingHint:
      "Adiciona o URL de embed do Google My Maps na constante MAP_EMBED_URL.",
  },
  en: {
    heading: "Map of the arraiais",
    description:
      "See the main Festas de Lisboa spots on one map to plan your route by neighbourhood.",
    iframeTitle: "Map of Festas de Lisboa arraiais",
    openMap: "Open in Google Maps",
    missingEmbed: "Map not configured yet",
    missingHint:
      "Add the Google My Maps embed URL to the MAP_EMBED_URL constant.",
  },
  fr: {
    heading: "Carte des arraiais",
    description:
      "Retrouve les principaux points des Festas de Lisboa sur une seule carte pour planifier ton parcours par quartier.",
    iframeTitle: "Carte des arraiais des Festas de Lisboa",
    openMap: "Ouvrir dans Google Maps",
    missingEmbed: "Carte pas encore configurée",
    missingHint:
      "Ajoute l'URL d'intégration Google My Maps dans la constante MAP_EMBED_URL.",
  },
};

function pickLang(code: string | undefined): Lang {
  if (code === "en" || code?.startsWith("en-")) return "en";
  if (code === "fr" || code?.startsWith("fr-")) return "fr";
  return "pt";
}

function mapViewerUrl(embedUrl: string): string {
  try {
    const parsed = new URL(embedUrl);
    parsed.searchParams.delete("output");
    return parsed.toString();
  } catch {
    return embedUrl;
  }
}

export function FestasLisboaMap({ lang }: Props) {
  const locale = pickLang(lang);
  const str = STR[locale];
  const embedUrl = MAP_EMBED_URL.trim();

  return (
    <section className="rounded-2xl border border-[var(--festas-red)]/30 bg-[var(--festas-cream)] p-4 sm:p-6">
      <header className="mb-4">
        <h3 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">
          {str.heading}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base">
          {str.description}
        </p>
      </header>

      {embedUrl ? (
        <>
          <div className="overflow-hidden rounded-xl border border-[var(--festas-red)]/20 bg-white shadow-sm">
            <iframe
              title={str.iframeTitle}
              src={embedUrl}
              className="block w-full border-0"
              style={{ aspectRatio: "16/10", minHeight: "400px" }}
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <p className="mt-3 text-sm text-muted-foreground">
            <a
              href={mapViewerUrl(embedUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline decoration-[var(--festas-red)]/40 underline-offset-4 hover:decoration-[var(--festas-red)]"
            >
              {str.openMap}
            </a>
          </p>
        </>
      ) : (
        <div className="rounded-xl border border-dashed border-[var(--festas-red)]/40 bg-white/80 p-6 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">{str.missingEmbed}</p>
          <p className="mt-1">{str.missingHint}</p>
        </div>
      )}
    </section>
  );
}
